import React, { useMemo } from 'react';

import { useQuery } from '@nerdwallet/apollo';
import GlobalMarkupOptions from '@nerdwallet/coin/GlobalMarkupOptions';

import Context, { useUrlRecordContext } from './context';
import convertURLRecordToSeoMetadata, {
  populateVariables,
} from './convert-url-record-to-seo-metadata';
import urlRecordSeoMetadataGql from './gql';

interface MetaDataOptions {
  nerdLayoutMappings: {
    pageTaxonomy: Record<string, any>;
    keywords?: string;
  };
  variables?: Record<string, any>;
  url?: string | ((url?: string) => string);
  imageType?: string;
}

interface UrlRecordOptions {
  url: (() => string) | string;
  metadataOptions: (props?: any) => MetaDataOptions;
  size?: string;
}

interface WithURLRecordProps {
  component: React.ElementType;
  options: UrlRecordOptions;
}

const WithURLRecord = ({
  component: Component,
  options,
  ...props
}: WithURLRecordProps): JSX.Element => {
  const { url, size = 'PRIMARY_XS_UNCROPPED', metadataOptions } = options;
  const parsedUrl = typeof url === 'function' ? url() : url;
  const parsedMetadataOptions =
    typeof metadataOptions === 'function'
      ? metadataOptions(props)
      : metadataOptions;

  const { data } = useQuery(urlRecordSeoMetadataGql, {
    variables: {
      url: parsedUrl,
      size,
    },
  });

  const urlRecordContextValue = useMemo(() => {
    const variables = parsedMetadataOptions?.variables;
    const title = populateVariables(data?.urlRecordByUrl?.title, variables);
    const synopsis = populateVariables(
      data?.urlRecordByUrl?.synopsis,
      variables
    );
    const primaryContent = data?.urlRecordByUrl?.primaryContent;

    return { title, synopsis, primaryContent };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);

  const seoMetadata = convertURLRecordToSeoMetadata(
    data?.urlRecordByUrl,
    parsedMetadataOptions
  );

  return (
    <React.Fragment>
      <GlobalMarkupOptions>{seoMetadata}</GlobalMarkupOptions>
      <Context.Provider value={urlRecordContextValue}>
        <Component {...props} />
      </Context.Provider>
    </React.Fragment>
  );
};

export { useUrlRecordContext };

export type { UrlRecordOptions, MetaDataOptions };

export default WithURLRecord;
